/* eslint-disable @tanstack/query/exhaustive-deps */
import { request } from '@/helpers/apiClient';
import { useQuery } from '@tanstack/react-query';
import { HTTP_METHODS } from '@/utils/constants/httpMethods';
import { FeatureConfig } from '@/utils/constants/featuresFlag';
import { formatDateParam } from '@/helpers/formatDateParam';
import { buildDateQueryString } from '@/helpers/buildDateQueryString';
import { buildFilterQueryString } from '@/helpers/buildFilterQueryString';
/**
 * API configuration for store-related endpoints
 */
export const STORE_CONFIG = {
  GET_STORE_BY_ID: storeId => ({
    method: HTTP_METHODS.GET,
    endPoint: `api/v1/adminui/store/${storeId}`,
    baseUrlKey: 'cepApiUrl',
    headers: {
      'x-api-key': import.meta.env[FeatureConfig.cepApiKey],
      siteId: import.meta.env[FeatureConfig.cepSiteId]
    }
  }),

  GET_STORE_LIST: (page = 0, pageSize = 10, filters = {}) => {
    let url = `api/v1/adminui/stores?page=${page}&pageSize=${pageSize}`;

    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: {
        'x-api-key': import.meta.env[FeatureConfig.cepApiKey],
        siteId: import.meta.env[FeatureConfig.cepSiteId]
      }
    };
  },

  GET_STORE_LIST_WITH_MISSING_PRODUCT_LIST: (
    page = 0,
    pageSize = 10,
    filters = {},
    startDate = null,
    endDate = null
  ) => {
    let url = `api/v1/adminui/storesMissingProductList?page=${page}&pageSize=${pageSize}`;

    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }

    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: {
        'x-api-key': import.meta.env[FeatureConfig.cepApiKey],
        siteId: import.meta.env[FeatureConfig.cepSiteId]
      }
    };
  },

  GET_STORE_LIST_WITH_MISSING_VISIT_LIST: (
    page = 0,
    pageSize = 10,
    filters = {},
    startDate = null,
    endDate = null
  ) => {
    let url = `api/v1/adminui/storesMissingVisitList?page=${page}&pageSize=${pageSize}`;

    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }

    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: {
        'x-api-key': import.meta.env[FeatureConfig.cepApiKey],
        siteId: import.meta.env[FeatureConfig.cepSiteId]
      }
    };
  },

  GET_STORE_LIST_WITH_MISSING_MOA: (
    page = 0,
    pageSize = 10,
    filters = {},
    startDate = null,
    endDate = null
  ) => {
    let url = `api/v1/adminui/storesMissingMOA?page=${page}&pageSize=${pageSize}`;

    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }

    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: {
        'x-api-key': import.meta.env[FeatureConfig.cepApiKey],
        siteId: import.meta.env[FeatureConfig.cepSiteId]
      }
    };
  },

  GET_STORE_LIST_WITH_MISSING_CUT_OFF_TIME: (
    page = 0,
    pageSize = 10,
    filters = {},
    startDate = null,
    endDate = null
  ) => {
    let url = `api/v1/adminui/storesMissingCutOffTime?page=${page}&pageSize=${pageSize}`;

    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }

    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: {
        'x-api-key': import.meta.env[FeatureConfig.cepApiKey],
        siteId: import.meta.env[FeatureConfig.cepSiteId]
      }
    };
  },

  GET_STORE_LIST_WITH_MISSING_DELIVERY_SERVICE: (
    page = 0,
    pageSize = 10,
    filters = {},
    startDate = null,
    endDate = null
  ) => {
    let url = `api/v1/adminui/storesMissingDeliveryService?page=${page}&pageSize=${pageSize}`;

    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }

    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: {
        'x-api-key': import.meta.env[FeatureConfig.cepApiKey],
        siteId: import.meta.env[FeatureConfig.cepSiteId]
      }
    };
  }
};

/**
 * Fetches store data by ID from the CEP API
 */
export const fetchStoreById = async storeId => {
  const response = await request(STORE_CONFIG.GET_STORE_BY_ID(storeId));
  return response;
};

/**
 * Fetches paginated store list from the CEP API
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {Promise} - Promise with store list data
 */
export const fetchStoreList = async (page = 0, pageSize = 10, filters = {}) => {
  return request(STORE_CONFIG.GET_STORE_LIST(page, pageSize, filters));
};

/**
 * Fetches paginated store list missing product list from the CEP API
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {Promise} - Promise with store list data
 */
export const fetchStoreListWithMissingProductList = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return request(
    STORE_CONFIG.GET_STORE_LIST_WITH_MISSING_PRODUCT_LIST(
      page,
      pageSize,
      filters,
      startDate,
      endDate
    )
  );
};

/**
 * Fetches paginated store list missing visit list from the CEP API
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {Promise} - Promise with store list data
 */
export const fetchStoreListWithMissingVisitList = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return request(
    STORE_CONFIG.GET_STORE_LIST_WITH_MISSING_VISIT_LIST(
      page,
      pageSize,
      filters,
      startDate,
      endDate
    )
  );
};

/**
 * Fetches paginated store list missing MOA from the CEP API
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {Promise} - Promise with store list data
 */
export const fetchStoreListWithMissingMOA = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return request(
    STORE_CONFIG.GET_STORE_LIST_WITH_MISSING_MOA(
      page,
      pageSize,
      filters,
      startDate,
      endDate
    )
  );
};

/**
 * Fetches paginated store list missing Cut Off Time from the CEP API
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {Promise} - Promise with store list data
 */
export const fetchStoreListWithMissingCutOffTime = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return request(
    STORE_CONFIG.GET_STORE_LIST_WITH_MISSING_CUT_OFF_TIME(
      page,
      pageSize,
      filters,
      startDate,
      endDate
    )
  );
};

/**
 * Fetches paginated store list missing Delivery Service from the CEP API
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {Promise} - Promise with store list data
 */
export const fetchStoreListWithMissingDeliveryService = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return request(
    STORE_CONFIG.GET_STORE_LIST_WITH_MISSING_DELIVERY_SERVICE(
      page,
      pageSize,
      filters,
      startDate,
      endDate
    )
  );
};

/**
 * React Query hook for retrieving and caching store data by ID
 */
export const useStoreData = storeId => {
  return useQuery({
    queryKey: ['storeData', storeId],
    queryFn: () => fetchStoreById(storeId),
    enabled: !!storeId
  });
};

/**
 * React Query hook for retrieving and caching paginated store list
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreList = (page = 0, pageSize = 10, filters = {}) => {
  return useQuery({
    queryKey: ['storeList', page, pageSize, filters],
    queryFn: () => fetchStoreList(page, pageSize, filters)
  });
};

/**
 * React Query hook for retrieving and caching paginated store list with empty products list
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreListWithMissingProductList = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);

  return useQuery({
    queryKey: [
      'storeListWithMissingProductList',
      page,
      pageSize,
      filters,
      formattedStartDate,
      formattedEndDate
    ],
    queryFn: () =>
      fetchStoreListWithMissingProductList(
        page,
        pageSize,
        filters,
        startDate,
        endDate
      )
  });
};

/**
 * React Query hook for retrieving and caching paginated store list with empty visit lists
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreListWithMissingVisitList = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);

  return useQuery({
    queryKey: [
      'storeListWithMissingVisitList',
      page,
      pageSize,
      filters,
      formattedStartDate,
      formattedEndDate
    ],
    queryFn: () =>
      fetchStoreListWithMissingVisitList(
        page,
        pageSize,
        filters,
        startDate,
        endDate
      )
  });
};

/**
 * React Query hook for retrieving and caching paginated store list with missing MOA
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreListWithMissingMOA = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);

  return useQuery({
    queryKey: [
      'storeListWithMissingMOA',
      page,
      pageSize,
      filters,
      formattedStartDate,
      formattedEndDate
    ],
    queryFn: () =>
      fetchStoreListWithMissingMOA(page, pageSize, filters, startDate, endDate)
  });
};

/**
 * React Query hook for retrieving and caching paginated store list with missing Cut Off Time
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreListWithMissingCutOffTime = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);

  return useQuery({
    queryKey: [
      'storeListWithMissingCutOffTime',
      page,
      pageSize,
      filters,
      formattedStartDate,
      formattedEndDate
    ],
    queryFn: () =>
      fetchStoreListWithMissingCutOffTime(
        page,
        pageSize,
        filters,
        startDate,
        endDate
      )
  });
};

/**
 * React Query hook for retrieving and caching paginated store list with missing Delivery Service
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useStoreListWithMissingDeliveryService = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);

  return useQuery({
    queryKey: [
      'storeListWithMissingDeliveryService',
      page,
      pageSize,
      filters,
      formattedStartDate,
      formattedEndDate
    ],
    queryFn: () =>
      fetchStoreListWithMissingDeliveryService(
        page,
        pageSize,
        filters,
        startDate,
        endDate
      )
  });
};
